import {
  Dispatch,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCalendlyEventListener } from "react-calendly";
import autosize from "autosize";
import { Output } from "valibot";

import ChatInputBox from "components/ChatInput";
import MessageBubble from "components/MessageBubble";
import ConnectingMessage from "components/MessageBubble/ConnectingMessage";
import PopupMessages from "components/PopupMessages";
import {
  AfterMessageQuestion,
  BotInfoSchema,
  ChatDataSchema,
  ChatUserFormFieldsSchema,
  InstalledApps,
  LiveAgentHandoffInfo,
  LocationBasedConsentInfo,
  SupportTicketInfoSchema,
} from "components/SonicBot/schemas";
import TicketInfo from "components/TicketInfo";
import UserDataForm from "components/UserDataForm";
import {
  ChatUserFormData,
  createUser,
  requestCallback,
  upsertChatIdToActiveCampaign,
} from "shared/api";
import {
  getLocalStorageValue,
  getSessionStorageValue,
  getUserDataStorageKey,
  getUserFormStorageKey,
  setLocalStorageValue,
  setSessionStorageValue,
} from "shared/functions";
import { useConfigContext } from "shared/hooks";
import { IntegrationType } from "shared/integrations/configs";
import { isShowLocationBasedConsentForm } from "shared/integrations/utils";
import { ActionsSchema } from "shared/schemas";
import { AppConfiguration, GenerationMode } from "shared/types";
import classNames from "shared/utils/classNames";
import { getChatPanelText } from "shared/utils/sourceUtils";
import botsonicLogoGrey from "assets/images/botsonic-logo-grey.svg";
import { ReactComponent as IconArrowUp } from "assets/images/icons/arrow-up.svg";

import { showUnderTrainingBot } from "./function";
import IntegrationInlineWidget from "./Integrations/IntegrationInlineWidget";
import LocationBasedConsentForm from "./LocationBasedConsentForm";
import TopBar from "./TopBar";
import { TopBarSupportTicket } from "./TopBar/TopBarSupportTicket";
import useHandleScroll from "./useHandleScroll";

import "regenerator-runtime";
import "./index.css";

type Props = {
  isBotDataTrained: boolean;
  botInfo: Output<typeof BotInfoSchema>;
  onSend: (message: string, chatUserId?: string) => void;
  processStoredStarterQuestion: () => void;
  generationMode: GenerationMode;
  setGenerationMode: Dispatch<SetStateAction<GenerationMode>>;
  message: string;
  chatData: Output<typeof ChatDataSchema>;
  setChatData: Dispatch<SetStateAction<any[]>>;
  setMessage: Dispatch<SetStateAction<string>>;
  resetChat: () => void;
  useAsPreview: boolean;
  isIframePreview: boolean;
  isWidgetPreview: boolean;
  afterMessageQuestions: AfterMessageQuestion[];
  chatId: string;
  config: AppConfiguration;
  eventSource: EventSource | null;
  chatUserFormFields: Output<typeof ChatUserFormFieldsSchema>;
  loadingChatUserFormFields: boolean;
  baseURL: string;
  token: string;
  widgetPosition: string | undefined;
  messageLengthWhenSkipped: number;
  setMessageLengthWhenSkipped: Dispatch<SetStateAction<number>>;
  onSuggestionClick: (
    afterMessageQuestion: AfterMessageQuestion,
  ) => Promise<void>;
  locationBasedConsentInfo?: Output<typeof LocationBasedConsentInfo>;
  installedApps: Output<typeof InstalledApps>;
  setUseWebsocketGeneration: Dispatch<SetStateAction<boolean>>;
  welcomeMessage: string;
  actions: Output<typeof ActionsSchema>;
  liveAgentHandoffInfo?: Output<typeof LiveAgentHandoffInfo> | null;
  setLiveAgentHandoffInfo: Dispatch<
    SetStateAction<Output<typeof LiveAgentHandoffInfo> | null>
  >;
  handleCancelHumanHandoff: () => void;
  handleSendIntegrationData: ({
    message,
    configType,
    integrationData,
    formData,
  }: {
    message?: string;
    configType: string;
    integrationData: any;
    formData?: { [key: string]: any };
  }) => Promise<void>;
  hasChatEnded: boolean;
};

function ChatPanel(props: Props) {
  const { isIframe, instanceName, autofocusOff } = useConfigContext();

  const {
    isBotDataTrained,
    botInfo,
    onSend,
    processStoredStarterQuestion,
    generationMode,
    setGenerationMode,
    message,
    chatData,
    setChatData,
    setMessage,
    resetChat,
    useAsPreview,
    isIframePreview,
    isWidgetPreview,
    afterMessageQuestions,
    chatId,
    config,
    eventSource,
    chatUserFormFields,
    loadingChatUserFormFields,
    baseURL,
    token,
    widgetPosition = "right",
    onSuggestionClick,
    locationBasedConsentInfo,
    messageLengthWhenSkipped,
    setMessageLengthWhenSkipped,
    installedApps,
    setUseWebsocketGeneration,
    welcomeMessage,
    actions,
    liveAgentHandoffInfo,
    setLiveAgentHandoffInfo,
    handleCancelHumanHandoff,
    handleSendIntegrationData,
    hasChatEnded,
  } = props;
  const [isIdentifiedUser, setIsIdentifiedUser] = useState<boolean>(false);
  const [chatUser, setChatUser] = useState<ChatUserFormData | undefined>();
  const [showLocationBasedConsentForm, setShowLocationBasedConsentForm] =
    useState<boolean>(false);
  const [showRequestCallbackForm, setShowRequestCallbackForm] =
    useState<boolean>(false);
  const [callbackRequested, setCallbackRequested] = useState<boolean>(false);

  const [followUpQuestions, setFollowUpQuestions] = useState<
    AfterMessageQuestion[]
  >([]);
  const [ticketInfo, setTicketInfo] = useState<Output<
    typeof SupportTicketInfoSchema
  > | null>();
  const [isMessageSectionMounted, setIsMessageSectionMounted] =
    useState<boolean>(false);

  const inputRef = useRef<any>(null);

  const {
    chatDataContainerRef,
    innerContainerRef,
    autoScrollEnabled,
    handleScroll,
    scrollToBottom,
  } = useHandleScroll(chatData);

  const lastFormFieldInfo = chatData.at(-1)?.user_form_info;

  const copyTexts = getChatPanelText(
    botInfo.bot_prompt_config.generation_language,
  );

  const showBotDataUnderTrainingMessage = showUnderTrainingBot(
    isIframe,
    isIframePreview,
    isWidgetPreview,
    isBotDataTrained,
  );

  const handleSubmit = ({
    message,
    skipSubmission,
    formFieldValue,
  }: {
    message: string;
    skipSubmission?: boolean;
    formFieldValue?: any;
  }) => {
    if (!skipSubmission && (!message || message === " ")) {
      return;
    } else if (lastFormFieldInfo) {
      const integrationInfo = chatData.at(-1)?.integration?.integration_info;
      handleSendIntegrationData({
        message,
        configType: integrationInfo?.config_type || "",
        integrationData: integrationInfo,
        formData: {
          ...lastFormFieldInfo,
          form_field: {
            ...lastFormFieldInfo.form_field,
            value: formFieldValue,
          },
          skip_submission: skipSubmission,
        },
      });
    } else if (!generationMode) {
      onSend(message, chatUser?._id || undefined);
    }
  };

  useEffect(() => {
    if (isMessageSectionMounted) {
      return;
    }

    setIsMessageSectionMounted(true);
    scrollToBottom();
  }, [isMessageSectionMounted, scrollToBottom]);

  useEffect(() => {
    setShowLocationBasedConsentForm(
      isShowLocationBasedConsentForm(botInfo.id, locationBasedConsentInfo),
    );
  }, [locationBasedConsentInfo, botInfo.id]);

  useEffect(() => {
    // set auto size to textarea
    const questionEls = document.querySelectorAll(".message");
    for (const questionEl of questionEls) {
      autosize(questionEl);
    }

    // checking if user identified
    const isIdentified =
      getLocalStorageValue(getUserDataStorageKey(instanceName, botInfo.id)) ||
      "null";
    const identifiedUser: ChatUserFormData = JSON.parse(isIdentified);
    if (identifiedUser) {
      setIsIdentifiedUser(true);
      setChatUser(identifiedUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Once the user writes the message, it should again focus the cursor in the textbox so user can keep typing
    if (!generationMode && !autofocusOff) {
      inputRef?.current?.focus();
    }
  }, [generationMode, autofocusOff]);

  useLayoutEffect(() => {
    if (inputRef.current) {
      if (!message) {
        inputRef.current.style.height = isIframe ? "46px" : "42px";
      }
      autosize(inputRef.current);
    }
  }, [isIframe, message]);

  useEffect(() => {
    // reset height that is changed automatically according to content
    if (message) {
      return;
    }
    const questionEls = document.querySelectorAll(".message");
    for (const questionEl of questionEls) {
      const el = questionEl as HTMLTextAreaElement;
      el.style.height = "";
      el.style.overflow = "hidden";
    }
  }, [message]);

  const activeCampaignUserEmail = useMemo(
    () =>
      actions
        .find(action =>
          action.commands.some(command => command.activeCampaignEmail),
        )
        ?.commands.find(command => command.activeCampaignEmail)
        ?.activeCampaignEmail,
    [actions],
  );

  useCalendlyEventListener({
    onEventScheduled: () => {
      handleSendIntegrationData({
        configType: IntegrationType.CALENDLY,
        integrationData: {
          event_booked: true,
        },
      });
      if (activeCampaignUserEmail) {
        upsertChatIdToActiveCampaign({
          token,
          chatId,
          email: activeCampaignUserEmail,
        });
      }
    },
  });

  const followUps = chatData.at(-1)?.follow_ups;

  useEffect(() => {
    setFollowUpQuestions(
      (followUps || []).map(followUpQuestion => ({
        ...followUpQuestion,
        type: "follow-up-question",
      })),
    );
  }, [followUps]);

  useEffect(() => {
    if (liveAgentHandoffInfo?.is_active) {
      setUseWebsocketGeneration(true);
    }
  }, [liveAgentHandoffInfo, setUseWebsocketGeneration]);

  const followUpOrStarterQuestions = followUpQuestions.length
    ? followUpQuestions
    : afterMessageQuestions;

  const isShowUserRequestCallbackButton = useMemo(() => {
    return installedApps?.some(
      app => app.config_type === "request_callback" && app.is_active,
    );
  }, [installedApps]);

  const hasFormItems =
    chatUserFormFields &&
    Array.isArray(chatUserFormFields?.data_fields) &&
    !!chatUserFormFields?.data_fields?.filter(item => !item.is_deleted).length;

  const storedFormConfiguration = getLocalStorageValue(
    getUserFormStorageKey(instanceName, botInfo.id),
  );

  const isFormConfigurationChanged =
    chatUserFormFields &&
    storedFormConfiguration &&
    JSON.stringify(chatUserFormFields) !== storedFormConfiguration;

  const suppressUserForm =
    isIdentifiedUser || config.hideUserForm || !hasFormItems;

  const showUserDataForm =
    showRequestCallbackForm ||
    (!botInfo.bot_info_config.hide_user_form &&
      (isFormConfigurationChanged || !suppressUserForm));

  const classNameChatPanelContainer = useMemo(() => {
    if (isIframe) {
      return "flex-1 flex flex-col h-screen md:h-[calc(100%-4rem)] relative";
    }
    return classNames(
      useAsPreview ? "chat-panel-content-preview" : "",
      "relative max-h-full xs:max-h-[704px]",
      "chat-panel-content",
    );
  }, [isIframe, useAsPreview]);

  const brandingBanner = useMemo(() => {
    return (
      <>
        {botInfo.bot_info_config.is_show_chat_sonic_brand && (
          <div
            className={classNames(
              "bottom-container",
              isIframe && "rounded-none bg-surface",
            )}
          >
            <a
              className="flex items-center justify-center gap-1 text-sm text-secondary no-underline"
              href="https://www.botsonic.com/"
              target="_blank"
              rel="noreferrer"
            >
              <div className="hidden sm:block">
                <p>{copyTexts.bottom_text_cta}</p>
              </div>
              <div className="sm:hidden">
                <p>{copyTexts.bottom_powered_by}</p>
              </div>
              <img src={botsonicLogoGrey} alt="Botsonic Logo" />
              <p className="font-semibold">Botsonic</p>
            </a>
          </div>
        )}
      </>
    );
  }, [
    botInfo.bot_info_config.is_show_chat_sonic_brand,
    copyTexts.bottom_powered_by,
    copyTexts.bottom_text_cta,
    isIframe,
  ]);

  const calendlyBookingLink = chatData.at(-1)?.integration?.link;
  if (calendlyBookingLink) {
    return (
      <div className={classNameChatPanelContainer}>
        <IntegrationInlineWidget
          chatbotColor={botInfo.bot_info_config.chatbot_color}
          integrationLink={calendlyBookingLink}
          token={token}
          activeCampaignUserEmail={activeCampaignUserEmail}
          handleChange={(eventBooked: boolean) => {
            handleSendIntegrationData({
              configType: IntegrationType.CALENDLY,
              integrationData: {
                event_booked: eventBooked,
              },
            });
          }}
        />
      </div>
    );
  }

  const handleAcceptConsentAgreement = () => {
    if (!locationBasedConsentInfo) {
      return;
    }
    setLocalStorageValue(
      `consentInfo__${botInfo.id}`,
      JSON.stringify({
        isConsentGiven: true,
        lastUpdatedAt: locationBasedConsentInfo.updated_at,
      }),
    );
    setShowLocationBasedConsentForm(false);
  };

  const handleChatUserCreation = async (formData: any) => {
    const response = await createUser({
      baseURL,
      token,
      data: formData,
      chatId,
    });
    setLocalStorageValue(
      getUserDataStorageKey(instanceName, botInfo.id),
      JSON.stringify(response),
    );
    setLocalStorageValue(
      getUserFormStorageKey(instanceName, botInfo.id),
      JSON.stringify(chatUserFormFields),
    );
    setChatUser(response);
    setIsIdentifiedUser(true);

    // Triggering this to update the chat user in the global state
    window.dispatchEvent(new Event("CHAT_USER_SET"));

    if (showRequestCallbackForm) {
      requestCallback({
        token,
        chatUserFormDataId: response?._id || "",
        chatId,
      });
    }
    setShowRequestCallbackForm(false);

    const storedQuestionKey = `starter_question_${botInfo.id}`;
    const storedQuestionData = getSessionStorageValue(storedQuestionKey);

    if (storedQuestionData) {
      try {
        const questionData = JSON.parse(storedQuestionData);

        if (questionData.type === "follow-up-question") {
          onSend(questionData.question ?? "", response?._id);
        } else {
          // Process regular starter question
          processStoredStarterQuestion();
        }

        // Clear the stored question after processing
        setSessionStorageValue(storedQuestionKey, "");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error processing stored starter question:", err);
      }
    }
  };

  const handleOnRequestCallbackClick = () => {
    if (callbackRequested) {
      return;
    }
    if (storedFormConfiguration) {
      requestCallback({
        token,
        chatUserFormDataId: chatUser?._id || "",
        chatId,
      });
      const message = copyTexts.formAlreadySubmittedMessage;
      setChatData([
        ...chatData,
        {
          message,
          sent: false,
          showLikeDislikeBtn: false,
        },
      ]);
      setCallbackRequested(true);
    } else {
      setShowRequestCallbackForm(true);
    }
  };

  const handleResetChat = () => {
    resetChat();
    setUseWebsocketGeneration(false);
    setLiveAgentHandoffInfo(null);
  };

  const showMessages = !showUserDataForm && !showLocationBasedConsentForm;

  if (!!ticketInfo) {
    return (
      <div className={classNameChatPanelContainer}>
        <TopBar
          botInfo={botInfo}
          messagesLength={chatData?.length || 0}
          startAgainLabel={copyTexts.start_again}
          copyTexts={copyTexts}
          resetChat={handleResetChat}
          config={config}
          chatId={chatId}
          messageLengthWhenSkipped={messageLengthWhenSkipped}
          setMessageLengthWhenSkipped={setMessageLengthWhenSkipped}
          showRequestCallbackButton={isShowUserRequestCallbackButton}
          handleOnRequestCallbackClick={handleOnRequestCallbackClick}
          customTopBar={
            <TopBarSupportTicket
              copyTexts={copyTexts}
              onBackClick={() => {
                setTicketInfo(null);
                setIsMessageSectionMounted(false);
              }}
              isIframe={isIframe}
            />
          }
        />
        <TicketInfo ticketInfo={ticketInfo} copyTexts={copyTexts} />
        {brandingBanner}
      </div>
    );
  }

  return (
    <div className={classNameChatPanelContainer}>
      <TopBar
        botInfo={botInfo}
        messagesLength={chatData?.length || 0}
        startAgainLabel={copyTexts.start_again}
        copyTexts={copyTexts}
        resetChat={handleResetChat}
        config={config}
        chatId={chatId}
        messageLengthWhenSkipped={messageLengthWhenSkipped}
        setMessageLengthWhenSkipped={setMessageLengthWhenSkipped}
        showRequestCallbackButton={isShowUserRequestCallbackButton}
        handleOnRequestCallbackClick={handleOnRequestCallbackClick}
      />

      {showLocationBasedConsentForm && (
        <div
          className={classNames("message-container", isIframe ? "mt-2" : "p-4")}
        >
          <LocationBasedConsentForm
            message={locationBasedConsentInfo?.message || ""}
            onAccept={handleAcceptConsentAgreement}
            botAvatar={botInfo.bot_info_config?.chatbot_avatar}
            copyTexts={copyTexts}
            isIframe={isIframe}
          />
        </div>
      )}

      {showUserDataForm && !showLocationBasedConsentForm && (
        <UserDataForm
          botInfo={botInfo}
          chatUserFormFields={chatUserFormFields}
          loadingChatUserFormFields={loadingChatUserFormFields}
          copyTexts={copyTexts}
          handleChatUserCreation={handleChatUserCreation}
        />
      )}
      {showBotDataUnderTrainingMessage && (
        <p className="bg-orange-500 px-8 py-2 text-center text-sm text-white">
          {copyTexts.botDataUntrainedMessage}
          <span className="block text-xs italic">
            {copyTexts.notVisibleToCustomerMessage}
          </span>
        </p>
      )}

      {showMessages && (
        <>
          <div
            ref={chatDataContainerRef}
            className={classNames(
              "message-container",
              isIframe ? "mt-2" : "p-4",
            )}
            onScroll={handleScroll}
          >
            {!chatData.length && (
              <MessageBubble
                id="-1"
                botInfo={botInfo}
                isSent={false}
                message={welcomeMessage || copyTexts.default_welcome_message}
                config={config}
                chatId={chatId}
                chatData={chatData}
                setChatData={setChatData}
                copyTexts={copyTexts}
              />
            )}

            <div
              ref={innerContainerRef}
              className={classNames(
                "flex grow flex-col gap-3",
                !chatData.length && "justify-end",
              )}
            >
              {chatData.map(
                (
                  {
                    id,
                    message,
                    sent,
                    sources,
                    showLikeDislikeBtn = true,
                    imageUrls,
                    additionalInfo,
                    likeStatus,
                    user_form_info,
                    submitted_form_field_data,
                    is_intermediate,
                    integration,
                    renderableSource,
                  },
                  idx,
                ) => {
                  if (sent === true && !message) {
                    return null;
                  }
                  showLikeDislikeBtn =
                    user_form_info ||
                    submitted_form_field_data ||
                    integration?.type ||
                    integration?.integration_info?.config_type
                      ? false
                      : showLikeDislikeBtn;
                  return (
                    <MessageBubble
                      key={idx.toString()}
                      id={id || `-1`}
                      botInfo={botInfo}
                      isSent={sent}
                      message={message}
                      imageUrls={imageUrls}
                      renderableSource={renderableSource}
                      sources={sources}
                      config={config}
                      chatId={chatId}
                      chatData={chatData}
                      setChatData={setChatData}
                      isLastMessage={chatData.length - 1 === idx}
                      copyTexts={copyTexts}
                      showLikeDislikeBtn={showLikeDislikeBtn}
                      chatMessageType={additionalInfo?.chatMessageType}
                      avatarUrl={additionalInfo?.agentInfo?.agent_avatar}
                      likeStatus={likeStatus}
                      isConversationalEndResponse={
                        sent ? true : !is_intermediate
                      }
                      submittedFormFieldData={submitted_form_field_data}
                      ticketInfo={additionalInfo?.createdTicketInfo}
                      onTicketInfoClick={(
                        ticketInfo: Output<typeof SupportTicketInfoSchema>,
                      ) => setTicketInfo(ticketInfo)}
                    />
                  );
                },
              )}

              {chatData.at(-1)?.sent === true && generationMode && (
                <MessageBubble
                  id="jumping-dots-container"
                  botInfo={botInfo}
                  isSent={false}
                  message=""
                  imageUrls={[]}
                  sources={[]}
                  config={config}
                  chatId={chatId}
                  chatData={chatData}
                  setChatData={setChatData}
                  copyTexts={copyTexts}
                  showLikeDislikeBtn={false}
                  isLastMessage
                />
              )}

              {(!!followUpQuestions.length ||
                (!chatData.length && !!afterMessageQuestions.length)) && (
                <div className="flex grow flex-col justify-end gap-0.5">
                  <PopupMessages
                    botInfo={botInfo}
                    afterMessageQuestions={followUpOrStarterQuestions}
                    onSuggestionClick={onSuggestionClick}
                    widgetPosition={widgetPosition}
                    chatDataLength={chatData?.length || 0}
                    isOverButton={false}
                    generationMode={generationMode}
                    welcomeMessage={
                      welcomeMessage || botInfo.bot_info_config.welcome_message
                    }
                  />
                </div>
              )}
            </div>
          </div>

          <div
            className={classNames(
              "relative",
              isIframe ? "form-container_Iframe" : "form-container",
            )}
          >
            {showMessages && !autoScrollEnabled && (
              <div className="absolute -top-10 right-5 z-10">
                <button
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-surface/50 shadow-md hover:shadow-lg focus:outline-none focus:ring-2"
                  style={{
                    borderColor: "var(--chatBotColor)",
                    color: "var(--chatBotColor)",
                  }}
                  onClick={scrollToBottom}
                >
                  <IconArrowUp className="rotate-180 text-black" />
                </button>
              </div>
            )}

            {hasChatEnded ? (
              <div className="flex flex-col items-center justify-center p-4 text-center">
                <p className="mb-2 text-gray-600">
                  This chat has been deactivated due to inactivity.
                </p>
                <p className="text-gray-600">
                  <span
                    className="cursor-pointer font-medium text-blue-600 hover:text-blue-800"
                    onClick={handleResetChat}
                  >
                    Click here
                  </span>
                  {" to start a new chat"}
                </p>
              </div>
            ) : (
              <ChatInputBox
                isIframe={isIframe}
                inputRef={inputRef}
                message={message}
                setMessage={setMessage}
                eventSource={eventSource}
                generationMode={generationMode}
                setGenerationMode={setGenerationMode}
                handleSubmit={handleSubmit}
                botInfo={botInfo}
                formFieldInfo={lastFormFieldInfo}
                copyTexts={copyTexts}
              />
            )}
          </div>
        </>
      )}

      {brandingBanner}

      {liveAgentHandoffInfo?.is_active &&
        !liveAgentHandoffInfo?.is_agent_connected && (
          <div className="absolute bottom-0 w-full">
            <ConnectingMessage
              message={copyTexts.connectingToAgentMessage}
              onCancelBtnClick={handleCancelHumanHandoff}
              copyTexts={copyTexts}
            />
          </div>
        )}
    </div>
  );
}

export default ChatPanel;
